import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shield, Trophy, Target, Flame, ListChecks, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/common/Avatar";
import { OverCard } from "../components/history/OverCard";

export default function AdminPlayerHistoryPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    if (!user.is_admin) { nav("/"); return; }
    api.get(`/admin/users/${id}/history`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.data?.detail || "Failed to load history");
        toast.error(e?.response?.data?.detail || "Failed to load history");
      })
      .finally(() => setFetching(false));
  }, [user, loading, id, nav]);

  if (loading || !user?.is_admin) return <div className="p-12 text-slate-400">Checking access…</div>;

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/admin/players" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to player admin
        </Link>
        <div className="tile p-6 border-rose-500/40 text-rose-300">{error}</div>
      </div>
    );
  }

  if (fetching || !data) return <div className="p-12 text-slate-400">Loading player history…</div>;

  const { user: player, groups } = data;
  const totalOvers = groups.length;
  const totalPoints = groups.reduce((s, g) => s + g.total_points, 0);
  const totalPicks = groups.reduce((s, g) => s + g.picks_count, 0);
  const totalCorrect = groups.reduce((s, g) => s + g.correct_count, 0);
  const accuracy = totalPicks > 0 ? Math.round((totalCorrect / totalPicks) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <Link to="/admin/players" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6" data-testid="admin-history-back">
        <ArrowLeft className="w-4 h-4" /> Back to player admin
      </Link>

      <div className="tile p-6 grain mb-6 flex items-start gap-5 flex-wrap">
        <Avatar userId={player.id} name={player.name} hasAvatar={data.user.has_avatar} version={data.user.avatar_version} size={72} />
        <div className="min-w-0 flex-1">
          <div className="section-label flex items-center gap-1"><Shield className="w-3 h-3" /> Admin lookup</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mt-1" data-testid="admin-history-player-name">{player.name}</h1>
          <div className="text-slate-400 text-sm flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{player.email}</div>
          <div className="text-[10px] font-mono-data text-slate-500 mt-1">ID: {player.id}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={<Trophy className="w-4 h-4 text-amber-400" />} label="Points" value={player.points} />
          <Stat icon={<Target className="w-4 h-4 text-emerald-400" />} label="Accuracy" value={`${accuracy}%`} />
          <Stat icon={<Flame className="w-4 h-4 text-rose-400" />} label="Streak" value={player.streak} />
          <Stat icon={<ListChecks className="w-4 h-4 text-sky-400" />} label="Picks" value={`${totalCorrect}/${totalPicks}`} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <BigStat label="Overs called" value={totalOvers} />
        <BigStat label="Points earned" value={totalPoints} />
        <BigStat label="Picks correct" value={`${totalCorrect}/${totalPicks}`} />
        <BigStat label="Accuracy" value={`${accuracy}%`} />
      </div>

      {groups.length === 0 ? (
        <div className="tile p-10 text-center" data-testid="admin-history-empty">
          <p className="text-slate-300">{player.name} hasn't locked in any market picks yet.</p>
        </div>
      ) : (
        <div className="space-y-4 stagger">
          {groups.map((g) => <OverCard key={`${g.match_id}-${g.over_number}`} g={g} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="tile-elev px-3 py-2 min-w-[120px]">
      <div className="flex items-center gap-1.5">{icon}<span className="section-label">{label}</span></div>
      <div className="metric-value text-lg mt-0.5">{value}</div>
    </div>
  );
}

function BigStat({ label, value }) {
  return (
    <div className="tile p-4">
      <div className="section-label">{label}</div>
      <div className="metric-value text-2xl mt-1">{value}</div>
    </div>
  );
}
